import { useSubjectList } from '@/hooks/use-subject-list';
import type { QuestionLabel } from '@/services/generatedAPIs.schemas';
import { useTranslation } from 'react-i18next';
import { isEN } from '@/utils/i18n';

/**
 * 纯函数版本：根据已传入的 subjectInfos 返回展示名称。
 * 适用于已持有 subjectInfos 的组件，或在事件回调等非 hook 场景中使用。
 */
export function getSubjectDisplayName(
  subjectInfos: QuestionLabel[],
  subjectId?: number,
  fallbackLabel?: string,
  language?: string
): string | undefined {
  const subject = subjectInfos.find((s) => s.id === subjectId);
  const displayName = subject
    ? (language === 'en' ? (subject.enName || subject.name) : subject.name)
    : undefined;

  return displayName || fallbackLabel;
}

/**
 * 返回科目下拉选项列表，label 自动按当前语言切换。
 */
export function useSubjectOptions(): { value: string; label: string }[] {
  const { i18n } = useTranslation();
  const { subjectInfos } = useSubjectList();

  return subjectInfos.map((s) => ({
    value: s.id!.toString(),
    label: isEN(i18n) ? (s.enName || s.name!) : s.name!,
  }));
}

/**
 * 根据 subjectId 返回前端展示用的科目名称，找不到时回退到后端 label。
 * 明确区分"前端展示名"和"后端原始 label"。
 */
export function useSubjectDisplayName(
  subjectId?: number,
  fallbackLabel?: string
): string | undefined {
  const { i18n } = useTranslation();
  const { subjectInfos } = useSubjectList();

  const subject = subjectInfos.find((s) => s.id === subjectId);
  const displayName = subject
    ? (isEN(i18n) ? (subject.enName || subject.name) : subject.name)
    : undefined;

  return displayName || fallbackLabel;
}
